/**
 * 55 — screen 2: THE SONG.
 *
 * The waveform, the versions, the votes, and the notes.
 *
 * ── The waveform, and the two rules that govern it ──────────────────────────
 *
 * 1. PEAKS COME FROM R2. WE NEVER DECODE AUDIO ON THE PHONE.
 *    `audiowaveform` already did this on the Mac and the answer is sitting in
 *    R2 as a few KB of JSON. Decoding a 20-minute jam in the browser to draw
 *    a picture of it is a 30-second stall and a plausible out-of-memory kill.
 *    wavesurfer is constructed with { media, peaks, duration } and nothing
 *    else — no `url`, ever, because `url` is what makes it fetch and decode.
 *
 * 2. WAVESURFER DRAWS. IT DOES NOT PLAY.
 *    It's handed the app's one long-lived <audio> element as `media`, so it
 *    renders a picture and moves a cursor, and playback stays where it belongs.
 *
 *    A landmine worth writing down: `ws.load()` calls `setSrc()`, and setSrc
 *    with an empty url calls `media.removeAttribute('src')` — which would
 *    silently kill playback of whatever is currently going. So we NEVER call
 *    ws.load() to swap peaks. We destroy the instance and build a new one.
 *    That's safe, because with an external media element wavesurfer's destroy()
 *    returns early and doesn't touch the element at all.
 */

import WaveSurfer from 'wavesurfer.js';
import {
  getSong,
  getPeaks,
  vote as apiVote,
  voteComment,
  addComment,
  editComment,
  deleteComment,
  setCanonical,
  editVersion,
  archiveVersion,
  getDawProjects,
  uploadDawFile,
  dawDownloadUrl,
  getMembers,
  getSongLyrics,
  putSongLyrics,
  setLyricsLock,
  getTakeLyrics,
  searchLyrics,
  transcribeTake,
  ApiError,
  type SongDetail,
  type SongListener,
  type Version,
  type Comment,
  type Vote,
  type Votes,
  type Voter,
  type Member,
  type DawProject,
  type TakeLyrics,
  type LyricLine,
  type LyricHit,
} from './api';
import * as player from './player';
import { setNoteMarks, setNowLyrics } from './nowdata';
import { isLyricOwner, openVetSheet } from './vetting';
import { mountMixer } from './mixer';
import { mountWorkbench } from './workbench';
import { currentMember } from './login';
import { createVinyl, type Vinyl } from './vinyl';
import {
  h,
  clear,
  icon,
  fmtTime,
  fmtDate,
  relDate,
  initials,
  plural,
  toast,
  sheet,
  promptSheet,
  formSheet,
  emptyState,
  skeleton,
  isLight,
  type SheetItem,
} from './ui';
import type { Screen } from './shelf';

/**
 * Icons for the note menu, self-contained. The bottom sheet has no svg fill/
 * stroke rule of its own, so — unlike the shared `icon.*` set, which leans on
 * its container's CSS — these bake in their own presentation attributes and
 * render correctly wherever they're dropped.
 */
const noteIcon = {
  goto: `<svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true"><path d="M8 5.5v13l11-6.5z"/></svg>`,
  edit: `<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M4 20l4-1 10-10-3-3L5 16z"/></svg>`,
  move: `<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="12" cy="12" r="8.5"/><path d="M12 7.5V12l3.2 2"/></svg>`,
  done: `<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7"/></svg>`,
  back: `<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M4 5h16v11H9l-5 4z"/></svg>`,
  del: `<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M5 7h14M9.5 7V5h5v2M7 7l1 12h8l1-12"/></svg>`,
  zone: `<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M4 8v8M20 8v8M4 12h16"/></svg>`,
};

/** unix seconds → 'YYYY-MM-DD' in local time, for an <input type="date">. */
function toDateInputValue(unix: number | null | undefined): string {
  if (!unix) return '';
  const d = new Date(unix * 1000);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** 'YYYY-MM-DD' → unix seconds (noon local, so no timezone day-shift). */
function dateInputToUnix(str: string): number | null {
  if (!str) return null;
  const t = new Date(`${str}T12:00:00`);
  return Number.isNaN(t.getTime()) ? null : Math.floor(t.getTime() / 1000);
}

/** A duration note shorter than this is really a moment — don't make a zone of it. */
const MIN_ZONE_S = 1;
/** How long a moment (point) note lingers in the live overlay after its instant. */
const POINT_WINDOW_S = 6;

interface State {
  data: SongDetail | null;
  error: string | null;
  /** which version the screen is pointed at — canonical by default */
  selected: string | null;
  peaks: number[][] | null;
  duration: number;
  peaksPending: boolean;
  /** notes ticked off are hidden until this is flipped on (see the notes section) */
  showDone: boolean;
  /** the studio saves for this song — null until loaded, then newest-first */
  daw: DawProject[] | null;
  /** an openDAW project upload is in flight */
  dawUploading: boolean;
  /** 0..1, the live upload progress */
  dawProgress: number;
  /** the "earlier saves" list is expanded */
  dawEarlierOpen: boolean;
  /** the "how this works" guide is expanded */
  dawHowOpen: boolean;
  /** the full band roster, for "who hasn't heard this". null until fetched. */
  members: Member[] | null;
}

/**
 * A lyric-search hit navigates to (possibly another) song and drops the playhead
 * on the matched line. Routing is by hash and the song screen is torn down and
 * rebuilt on navigation, so we stash the intent at module scope (it outlives the
 * remount) and the freshly-mounted screen consumes it once its data has loaded.
 */
interface LyricSeekIntent {
  songId: string;
  takeId: string;
  start_s: number;
}
let pendingLyricSeek: LyricSeekIntent | null = null;

/** True when the viewer has asked for less motion — used for scroll behaviour. */
function prefersReducedMotion(): boolean {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}

export function mountSong(host: HTMLElement, songId: string, onBack: () => void): Screen {
  const state: State = {
    data: null,
    error: null,
    selected: null,
    peaks: null,
    duration: 0,
    peaksPending: false,
    showDone: false,
    daw: null,
    dawUploading: false,
    dawProgress: 0,
    dawEarlierOpen: false,
    dawHowOpen: false,
    members: null,
  };

  const abort = new AbortController();
  let ws: WaveSurfer | null = null;
  let vinyl: Vinyl | null = null;

  const body = h('div');
  const back = h('button', { class: 'iconbtn', html: icon.back, 'aria-label': 'Back to the shelf' });
  back.addEventListener('click', onBack);

  const titleEl = h('h1', { text: '' });

  /* The workbench door. Ugly and powerful; only Jonathan ever sees it, so the
     ⋯ stays hidden until currentMember() comes back with role==='admin'. */
  const more = h('button', {
    class: 'iconbtn',
    html: icon.more,
    'aria-label': 'Workbench',
    hidden: true,
  });
  more.addEventListener('click', openWorkbench);

  const top = h('header', { class: 'topbar' }, [back, titleEl, more]);
  host.append(top, body);

  const sel = (): Version | null =>
    state.data?.versions.find((v) => v.id === state.selected) ?? null;

  /* ---------------------------------------------------------- the workbench */

  let wbTeardown: (() => void) | null = null;

  function openWorkbench(): void {
    if (wbTeardown) return;
    // Hide the song, don't tear it down — the workbench mounts alongside it and
    // borrows the app's one <audio> element. It hands the audio back on close.
    top.hidden = true;
    body.hidden = true;
    wbTeardown = mountWorkbench(host, songId);
  }

  function closeWorkbench(): void {
    if (!wbTeardown) return;
    wbTeardown();
    wbTeardown = null;
    top.hidden = false;
    body.hidden = false;
    // The workbench can change takes, versions and which one is canonical, so
    // reload rather than trust the stale render underneath it.
    void load();
  }

  // The workbench bubbles this out of its own root when its back arrow is
  // pressed at the top level; we own routing, it just says it's done.
  host.addEventListener('workbench:close', closeWorkbench);

  // Who I am — used to relabel my own votes as "You" and to place my thumb in
  // the voter lists optimistically. Resolves fast (a cached /api/me); when it
  // lands we repaint so any already-drawn voter lines pick up the "You".
  let me: Member | null = null;
  void (async () => {
    me = await currentMember();
    if (abort.signal.aborted) return;
    if (me?.role === 'admin') more.hidden = false;
    if (state.data) render();
    // The admin-only "Transcribe this take" lives in the lyrics empty state; if
    // identity landed after it was painted, repaint that panel so it appears.
    maybeRepaintLyricAdmin();
  })();

  /* ------------------------------------------------------------ the mixer */

  // A stable host the mixer mounts into. The mixer owns BOTH tiers — the Tier-1
  // mix presets ("turn the drums off", plays everywhere) and the Tier-2 live
  // layer mixer — and fetches its own presets/layers, so the song screen no
  // longer hand-rolls a preset list. It is mounted ONCE per take and only
  // re-mounted when the selected version's take actually changes, so the
  // frequent re-renders this screen does (vote, note, canonical) never tear
  // down a running mixer engine.
  const mixHost = h('div', { class: 'mix-host' });
  let mixTeardown: (() => void) | null = null;
  let mixTakeId: string | null = null;

  function syncMixer(): void {
    const takeId = sel()?.take_id || null;
    if (takeId === mixTakeId) return;
    mixTeardown?.();
    mixTeardown = null;
    clear(mixHost);
    mixTakeId = takeId;
    if (takeId) mixTeardown = mountMixer(mixHost, takeId, player.element());
  }

  /* ------------------------------------------------------------- the studio */
  /*
   * Where the band works on this song in openDAW and passes the session around.
   * openDAW only saves a project bundle to the desktop, so the round trip is by
   * hand: download the newest save, open it in openDAW, work, save, come back and
   * upload it as the newest studio save. Every upload is a new row — nothing is
   * ever lost. This lives in its OWN stable host (like the mixer) so an unrelated
   * re-render — a vote, a note — never tears down an upload's progress bar, and a
   * finished upload repaints only this block, not the whole screen and its wave.
   */

  const studioHost = h('div', { class: 'studio-host' });

  async function loadDaw(): Promise<void> {
    try {
      const projects = await getDawProjects(songId, abort.signal);
      if (abort.signal.aborted) return;
      state.daw = projects;
    } catch {
      if (abort.signal.aborted) return;
      // Never block the screen on this — an empty studio still works.
      state.daw = [];
    }
    renderStudio();
  }

  /** "saved by Rob, 2 days ago" — name in type, time as a person would say it. */
  function savedLine(p: DawProject): string {
    const who = p.saved_by.name?.trim();
    const when = relDate(p.saved_at);
    if (who && when) return `saved by ${who}, ${when}`;
    if (who) return `saved by ${who}`;
    if (when) return `saved ${when}`;
    return 'saved';
  }

  /** Fetch the presigned URL and let the browser save it (content-disposition
   *  attachment is set server-side, so this downloads rather than navigates). */
  async function downloadProject(p: DawProject): Promise<void> {
    try {
      const url = await dawDownloadUrl(p.id, abort.signal);
      if (!url) throw new ApiError(0, 'NOT_FOUND');
      const a = document.createElement('a');
      a.href = url;
      a.rel = 'noopener';
      a.download = p.filename || '';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      if (abort.signal.aborted) return;
      toast(e instanceof ApiError ? e.human : "Couldn't get that file. Try again?", true);
    }
  }

  /* One persistent hidden picker for the whole screen — no extension filter,
     because openDAW's bundle extension is not fixed (.od / .odb / …). Reused
     across picks (value reset each time) so it never leaks a stray input, and
     kept in the DOM so old phones open the sheet reliably. */
  const dawFileInput = h('input', {
    type: 'file',
    'aria-label': 'Choose your saved openDAW project',
  }) as HTMLInputElement;
  dawFileInput.style.display = 'none';
  dawFileInput.addEventListener('change', () => {
    const file = dawFileInput.files?.[0];
    dawFileInput.value = ''; // picking the same file twice must still fire
    if (!file) return;
    void (async () => {
      const song = state.data?.song;
      const note = await promptSheet({
        title: 'Save to the band',
        label: 'What did you do? (optional)',
        placeholder: 'tightened the chorus, added Rob’s solo',
        multiline: true,
        confirm: 'Upload',
      });
      // The note is optional, so a blank one is fine — we still upload. The
      // title defaults to the song name; nobody has to type it.
      await startUpload(file, note, song?.title ?? file.name);
    })();
  });
  host.appendChild(dawFileInput);

  /** Open the file picker; the change handler above carries it from there. */
  function pickAndUpload(): void {
    dawFileInput.click();
  }

  async function startUpload(file: File, note: string | null, title: string): Promise<void> {
    const song = state.data?.song;
    state.dawUploading = true;
    state.dawProgress = 0;
    renderStudio();

    try {
      const proj = await uploadDawFile(file, {
        songId: song?.id ?? null,
        title,
        note,
        signal: abort.signal,
        onProgress: (f) => {
          state.dawProgress = f;
          paintDawProgress(); // cheap: nudges the bar, no full re-render
        },
      });
      if (abort.signal.aborted) return;
      state.daw = [proj, ...(state.daw ?? [])];
      state.dawUploading = false;
      renderStudio();
      toast('Saved. The band can carry on from here.');
    } catch (e) {
      if (abort.signal.aborted) return;
      state.dawUploading = false;
      renderStudio();
      toast(e instanceof ApiError ? e.human : "That didn't upload. Try again?", true);
    }
  }

  /** Move the live progress bar without rebuilding the block (called per tick). */
  function paintDawProgress(): void {
    const pct = Math.round(state.dawProgress * 100);
    const bar = studioHost.querySelector<HTMLElement>('.studio-progress-bar');
    if (bar) bar.style.width = `${pct}%`;
    const label = studioHost.querySelector<HTMLElement>('.studio-progress-pct');
    if (label) label.textContent = `${pct}%`;
  }

  function saveRow(p: DawProject, latest: boolean): HTMLElement {
    const dl = h('button', {
      class: latest ? 'btn-play studio-download' : 'btn-sm studio-download-sm',
      'aria-label': `Download ${p.title} to open in openDAW`,
      text: latest ? 'DOWNLOAD TO OPEN IN openDAW' : 'Download',
    });
    dl.addEventListener('click', () => void downloadProject(p));

    const meta: (Node | string)[] = [
      h('div', { class: 'studio-save-title', text: p.title || 'Studio save' }),
      h('div', { class: 'studio-save-meta', text: savedLine(p) }),
    ];
    if (p.note) meta.push(h('p', { class: 'studio-save-note', text: p.note }));

    return h('div', { class: latest ? 'studio-latest' : 'studio-earlier-row' }, [
      h('div', { class: 'studio-save-head' }, meta),
      dl,
    ]);
  }

  function renderStudio(): void {
    clear(studioHost);

    studioHost.appendChild(
      h('div', { class: 'section-h' }, [h('h2', { text: 'Studio' })]),
    );

    const card = h('div', { class: 'studio' });

    // The explainer + the one door out to openDAW (a real anchor, new tab).
    card.appendChild(
      h('p', {
        class: 'studio-explain',
        text: 'Work on this song in openDAW, then pass the session back to the band.',
      }),
    );
    card.appendChild(
      h('a', {
        class: 'studio-open',
        href: 'https://daw.thedevilssons.ca',
        target: '_blank',
        rel: 'noopener',
      }, [
        h('span', { text: 'Open openDAW' }),
        h('span', { class: 'studio-open-go', 'aria-hidden': 'true', text: '↗' }),
      ]),
    );

    const saves = state.daw ?? [];

    // The latest save — the one the next person carries on from.
    if (saves.length > 0) {
      card.appendChild(saveRow(saves[0]!, true));
    } else if (state.daw !== null) {
      card.appendChild(
        h('p', {
          class: 'studio-none',
          text: 'No studio saves yet. Open openDAW, work on this song, then upload your saved project below so the band can carry on.',
        }),
      );
    }

    // Older saves, collapsed. Every one is downloadable, with who/when/note.
    const older = saves.slice(1);
    if (older.length > 0) {
      const toggle = h('button', {
        class: 'studio-earlier-toggle',
        'aria-expanded': state.dawEarlierOpen ? 'true' : 'false',
        text: state.dawEarlierOpen ? 'Hide earlier saves' : `Earlier saves (${older.length})`,
      });
      toggle.addEventListener('click', () => {
        state.dawEarlierOpen = !state.dawEarlierOpen;
        renderStudio();
      });
      card.appendChild(toggle);

      if (state.dawEarlierOpen) {
        const list = h('div', { class: 'studio-earlier' });
        for (const p of older) list.appendChild(saveRow(p, false));
        card.appendChild(list);
      }
    }

    // Upload — the live progress bar takes the button's place while it runs.
    if (state.dawUploading) {
      const pct = Math.round(state.dawProgress * 100);
      card.appendChild(
        h('div', { class: 'studio-progress', role: 'status', 'aria-label': 'Uploading your project' }, [
          h('div', { class: 'studio-progress-top' }, [
            h('span', { text: 'Uploading your project…' }),
            h('span', { class: 'studio-progress-pct', text: `${pct}%` }),
          ]),
          h('div', { class: 'studio-progress-track' }, [
            (() => {
              const bar = h('div', { class: 'studio-progress-bar' });
              bar.style.width = `${pct}%`;
              return bar;
            })(),
          ]),
        ]),
      );
    } else {
      const up = h('button', {
        class: 'studio-upload',
        text: 'UPLOAD YOUR SAVED PROJECT',
      });
      up.addEventListener('click', pickAndUpload);
      card.appendChild(up);
    }

    // "How this works" — the round trip in plain, numbered words.
    const howToggle = h('button', {
      class: 'studio-howtoggle',
      'aria-expanded': state.dawHowOpen ? 'true' : 'false',
      text: state.dawHowOpen ? 'Hide how this works' : 'How this works',
    });
    howToggle.addEventListener('click', () => {
      state.dawHowOpen = !state.dawHowOpen;
      renderStudio();
    });
    card.appendChild(howToggle);

    if (state.dawHowOpen) {
      const steps = [
        'Download a recording, or the latest studio save, to your computer.',
        'Open openDAW. Import the audio, or open the project you downloaded.',
        'Work on it. In openDAW, hit Save — it saves the project to your computer.',
        'Come back here and upload that saved file so the band can carry on.',
        'To share a mix for listening, export a WAV or MP3 from openDAW and add it with the + button.',
      ];
      const ol = h('ol', { class: 'studio-guide' });
      for (const s of steps) ol.appendChild(h('li', { text: s }));
      card.appendChild(ol);
    }

    studioHost.appendChild(card);
  }

  /* -------------------------------------------------- who's heard this (J-room) */
  /*
   * The whole point, made plain: at a glance, who has and hasn't heard this song.
   * It reads warm — "who's caught up", never "who's slacking". Everyone who's
   * listened gets a bone disc, their play count and when they last heard it; the
   * rest of the band sits below, dimmed, at "not yet" — so a note left here has an
   * honest answer to "has everyone listened?". Its own stable host, like the
   * studio, so a vote or a note never tears it down, and the roster arriving late
   * repaints only this block. Never surveillant: no play-by-play, just a tally.
   */

  const heardHost = h('div', { class: 'heard-host' });

  async function loadMembers(): Promise<void> {
    // The roster barely changes; fetch it once and repaint from cache thereafter.
    if (state.members) {
      renderHeard();
      return;
    }
    try {
      const members = await getMembers(abort.signal);
      if (abort.signal.aborted) return;
      state.members = members;
    } catch {
      if (abort.signal.aborted) return;
      // No roster is a fine outcome — we still show whoever has listened.
      state.members = [];
    }
    renderHeard();
  }

  /** One person who's heard it: bone disc, name, "2 plays · yesterday". */
  function heardRow(l: SongListener): HTMLElement {
    const bits = [plural(l.plays, 'play')];
    if (l.last_at) bits.push(relDate(l.last_at));
    const meta = bits.join(' · ');
    return h(
      'li',
      { class: 'heard-row', 'data-heard': '1', 'aria-label': `${l.name} — ${meta}` },
      [
        h('span', { class: 'heard-disc', 'aria-hidden': 'true', text: initials(l.name) }),
        h('span', { class: 'heard-who' }, [
          h('span', { class: 'heard-name', text: l.name }),
          h('span', { class: 'heard-meta', text: meta }),
        ]),
      ],
    );
  }

  /** Someone from the band who hasn't heard it yet — dimmed, hollow disc. */
  function notYetRow(m: Member): HTMLElement {
    return h(
      'li',
      { class: 'heard-row', 'data-heard': '0', 'aria-label': `${m.name} hasn't heard this yet` },
      [
        h('span', { class: 'heard-disc heard-disc--empty', 'aria-hidden': 'true', text: initials(m.name) }),
        h('span', { class: 'heard-who' }, [
          h('span', { class: 'heard-name', text: m.name }),
          h('span', { class: 'heard-meta', text: 'not yet' }),
        ]),
      ],
    );
  }

  function renderHeard(): void {
    clear(heardHost);
    const data = state.data;
    if (!data) return;

    const listeners = data.listeners ?? [];
    const roster = state.members ?? [];
    const heardIds = new Set(listeners.map((l) => l.member_id));
    const notYet = roster.filter((m) => !heardIds.has(m.id));

    // Nothing known yet (roster still loading, nobody on record) — wait quietly
    // rather than flash an empty section that fills a beat later.
    if (listeners.length === 0 && roster.length === 0) return;

    const total = data.total_plays ?? listeners.reduce((n, l) => n + l.plays, 0);
    const head = h('div', { class: 'section-h heard-head' }, [h('h2', { text: "Who's heard this" })]);
    if (total > 0) head.appendChild(h('span', { class: 'heard-total', text: plural(total, 'play') }));
    heardHost.appendChild(head);

    const ul = h('ul', { class: 'heard-list' });
    for (const l of listeners) ul.appendChild(heardRow(l)); // already most-recent-first
    for (const m of notYet) ul.appendChild(notYetRow(m));
    heardHost.appendChild(ul);
  }

  /* ================================================================== lyrics */
  /*
   * The star of this screen: Apple-Music auto-scrolling lyrics, timed to the
   * take that's playing. Timing is ALWAYS per-take (getTakeLyrics uses the
   * selected version's take_id) — takes differ in tempo and arrangement, so
   * there is never one central set of timings. Brad edits the CLEAN master words
   * (songs.lyrics) and the Worker re-aligns; we just re-fetch.
   *
   * Like the studio/heard blocks this lives in its OWN stable host, so a vote or
   * a note elsewhere on the screen never tears down a scroll that's mid-song or a
   * search box someone is typing in. The heavy work — building the line stack — is
   * done ONLY when the take or its lines actually change (see renderLyrics), so
   * the frequent re-renders never reset the active line or the scroll position.
   */

  const lyricsHost = h('div', { class: 'lyrics-host' });
  const lyricBody = h('div', { class: 'lyr-body' });
  const lyrLockHost = h('div', { class: 'lyr-lock' });

  let lyricsBuilt = false;
  let lyricLockBusy = false;
  /** the take whose lines are currently loaded (or being loaded) */
  let lyricsTakeId: string | null = null;
  let lyricsData: TakeLyrics | null = null;
  /** Brad's master words, fetched lazily when the editor opens; cached after. */
  let lyricsMaster: string | null = null;
  let lyricsMasterLoaded = false;
  let lyricsTranscribing = false;

  /** the rendered line buttons, in order — the tick handler drives their state */
  let lyricEls: HTMLElement[] = [];
  let lyricScrollEl: HTMLElement | null = null;
  let lyricActiveIdx = -1;

  let lyricResultsEl: HTMLElement | null = null;
  let searchTimer = 0;
  let searchAbort: AbortController | null = null;

  /** Build the section shell ONCE: heading, the line area, the editor, search. */
  function buildLyricsShell(): void {
    lyricsHost.appendChild(h('div', { class: 'section-h' }, [h('h2', { text: 'Lyrics' })]));
    lyricsHost.appendChild(lyrLockHost);
    lyricsHost.appendChild(lyricBody);

    // "Edit lyrics" — any member can, but it's framed as Brad's job to keep.
    const edit = h('button', { class: 'lyr-editbtn', 'aria-label': 'Edit the master lyrics' }, [
      h('span', { class: 'lyr-editbtn-ic', html: icon.pencil, 'aria-hidden': 'true' }),
      h('span', { class: 'lyr-editbtn-txt' }, [
        h('span', { class: 'lyr-editbtn-label', text: 'Edit lyrics' }),
        h('span', { class: 'lyr-editbtn-hint', text: 'the master words — Brad’s to keep' }),
      ]),
    ]);
    edit.addEventListener('click', () => void openLyricEditor());
    lyricsHost.appendChild(edit);

    lyricsHost.appendChild(buildLyricSearch());
  }

  /**
   * Called from render() (cheap) and after a version switch. Only kicks a fetch
   * + repaint when the SELECTED take actually changes — otherwise it leaves the
   * live line stack, its highlight and its scroll exactly where they are.
   */
  function renderLyrics(): void {
    if (!lyricsBuilt) {
      buildLyricsShell();
      lyricsBuilt = true;
    }
    const take = sel()?.take_id ?? null;
    if (take !== lyricsTakeId) {
      lyricsTakeId = take;
      lyricsData = null;
      lyricsTranscribing = false;
      paintLyricBody(); // loading placeholder
      void loadLyrics(take);
    }
  }

  /** Fetch the timed lines for a take and repaint. Safe against a stale answer. */
  async function loadLyrics(take: string | null): Promise<void> {
    if (!take) {
      lyricsData = { lines: [], source: 'none', transcribed: false, has_master: false };
      paintLyricBody();
      return;
    }
    try {
      const r = await getTakeLyrics(take, abort.signal);
      if (abort.signal.aborted) return;
      if ((sel()?.take_id ?? null) !== take) return; // selection moved on while we waited
      lyricsData = r;
      // Mirror the take's synced lines to the footer strip (keyed by the version
      // that owns this take, which is what's playing).
      if (state.selected) setNowLyrics(state.selected, r.lines.map((l) => ({ text: l.text, start_s: l.start_s })));
      if (r.has_master && !lyricsMasterLoaded) {
        // The take knows the song has words even before we've fetched them.
        lyricsMaster = lyricsMaster ?? '';
      }
    } catch {
      if (abort.signal.aborted) return;
      lyricsData = { lines: [], source: 'none', transcribed: false, has_master: !!lyricsMaster };
    }
    paintLyricBody();
  }

  /**
   * Rebuild the line area from lyricsData. This is the ONLY place that touches
   * the line DOM, and it runs only when the data changed — so the tick handler's
   * cached element list is always in step with what's on screen.
   */
  function paintLyricBody(): void {
    lyricEls = [];
    lyricScrollEl = null;
    lyricActiveIdx = -1;
    clear(lyricBody);
    renderLyricLock();

    if (lyricsData == null) {
      lyricBody.appendChild(h('div', { class: 'lyr-loading', text: 'Loading the words…' }));
      return;
    }

    const lines = lyricsData.lines;
    if (lines.length > 0) {
      const list = h('div', { class: 'lyr-lines' });
      for (const line of lines) {
        const el = h('button', {
          class: 'lyr-line',
          'data-idx': String(line.idx),
          text: line.text || '♪',
          'aria-label': `Play from “${line.text}”`,
        });
        el.addEventListener('click', () => onLyricTap(line));
        list.appendChild(el);
        lyricEls.push(el);
      }
      const scroll = h('div', { class: 'lyr-scroll' }, [list]);
      lyricScrollEl = scroll;
      lyricBody.appendChild(scroll);

      // If this take is the one playing, light the right line immediately (no
      // animated scroll on first paint — jump straight to it).
      const v = sel();
      if (v && player.isCurrent(v.id)) syncLyrics(player.get().currentTime, true);
      return;
    }

    lyricBody.appendChild(lyricEmptyState());
  }

  /**
   * The lock control + the per-take "differs from the locked words" hint. Only
   * Brad/Johhny can lock; locking is what turns on vetting for future takes.
   */
  function renderLyricLock(): void {
    clear(lyrLockHost);
    const d = lyricsData;
    if (!d) return;
    const owner = isLyricOwner(me);
    const hasMaster = d.has_master || d.lines.length > 0;

    // This take's words diverge from the locked master — surface it right here.
    if (d.flag?.open) {
      const n = d.flag.n_diff;
      const hint = h('div', { class: 'lyr-flaghint' }, [
        h('span', {
          class: 'lyr-flaghint-txt',
          text: `These words differ from the locked lyrics${n ? ` on ${n} line${n === 1 ? '' : 's'}` : ''}.`,
        }),
      ]);
      if (owner) {
        const b = h('button', { class: 'btn-sm lyr-vetbtn', text: 'Vet it' });
        b.addEventListener('click', () => void openVetSheet((hash) => { location.hash = hash; }));
        hint.appendChild(b);
      }
      lyrLockHost.appendChild(hint);
    }

    if (owner && hasMaster) {
      const locked = !!d.locked;
      const row = h('div', { class: 'lyr-lockrow', 'data-locked': locked ? '1' : '0' }, [
        h('span', { class: 'lyr-lockstate', text: locked ? 'Locked — new takes get vetted' : 'Unlocked' }),
      ]);
      const btn = h('button', {
        class: `btn-sm lyr-lockbtn${locked ? '' : ' primary'}`,
        text: locked ? 'Unlock' : 'Lock lyrics',
      });
      (btn as HTMLButtonElement).disabled = lyricLockBusy;
      btn.addEventListener('click', () => void toggleLock(!locked));
      row.appendChild(btn);
      lyrLockHost.appendChild(row);
    } else if (!owner && d.locked) {
      lyrLockHost.appendChild(
        h('div', { class: 'lyr-lockrow', 'data-locked': '1' }, [
          h('span', { class: 'lyr-lockstate', text: 'Locked by Brad or Johhny' }),
        ]),
      );
    }
  }

  async function toggleLock(lock: boolean): Promise<void> {
    const song = state.data?.song;
    if (!song) return;
    lyricLockBusy = true;
    renderLyricLock();
    try {
      await setLyricsLock(song.id, lock);
      if (lyricsData) lyricsData.locked = lock;
      toast(lock ? 'Locked. New takes will be vetted against these words.' : 'Unlocked.');
    } catch (e) {
      toast(e instanceof ApiError ? e.human : "That didn't save. Try again?", true);
    } finally {
      lyricLockBusy = false;
      renderLyricLock();
    }
  }

  /** The "no lines yet" panel — the message, and the ways forward. */
  function lyricEmptyState(): HTMLElement {
    const d = lyricsData!;
    const wrap = h('div', { class: 'lyr-empty' });

    if (lyricsTranscribing) {
      wrap.append(
        h('div', { class: 'lyr-empty-title', text: 'Listening to this take…' }),
        h('p', {
          class: 'lyr-empty-sub',
          text: 'Picking out the words and their timings. This can take a moment.',
        }),
      );
      return wrap;
    }

    const hasMaster = d.has_master || !!(lyricsMaster && lyricsMaster.trim());
    if (hasMaster) {
      wrap.append(
        h('div', { class: 'lyr-empty-title', text: 'Lyrics are being written…' }),
        h('p', {
          class: 'lyr-empty-sub',
          text: 'The words are written — they just haven’t been timed to this take yet.',
        }),
      );
    } else {
      wrap.append(
        h('div', { class: 'lyr-empty-title', text: 'No lyrics yet' }),
        h('p', { class: 'lyr-empty-sub', text: 'Nobody has written the words for this one.' }),
      );
      const write = h('button', { class: 'btn-sm primary lyr-writebtn', text: 'Write the lyrics' });
      write.addEventListener('click', () => void openLyricEditor());
      wrap.appendChild(write);
    }

    // Admins can kick off the per-take transcription right here.
    if (me?.role === 'admin' && sel()?.take_id) {
      const btn = h('button', { class: 'btn-sm lyr-transcribe', text: 'Transcribe this take' });
      btn.addEventListener('click', () => void runTranscribe());
      wrap.appendChild(btn);
    }

    return wrap;
  }

  /** Repaint the empty-state panel so a late-arriving admin identity reveals the
   *  "Transcribe this take" button. A hoisted declaration so the me-resolver up
   *  top can call it before this section's `let`s are textually reached. */
  function maybeRepaintLyricAdmin(): void {
    if (lyricsBuilt && lyricsData && lyricsData.lines.length === 0) paintLyricBody();
  }

  /** Tap a line → play from it (or just seek, if this take is already playing). */
  function onLyricTap(line: LyricLine): void {
    const v = sel();
    if (!v) return;
    if (player.isCurrent(v.id)) player.seek(line.start_s);
    else void playVersion(v, line.start_s);
  }

  /**
   * The karaoke driver — called every animation frame while something plays.
   * Pure "which line is live at time t": finds the last line to have started,
   * marks past/active/upcoming, and keeps the active line centered. Seeks come
   * for free (player.seek pushes a tick), and it only touches the DOM when the
   * active line actually changes, so 60fps costs nothing.
   */
  function syncLyrics(t: number, instant = false): void {
    const v = sel();
    const current = !!v && player.isCurrent(v.id);
    const lines = lyricsData?.lines ?? [];
    if (!current || lyricEls.length === 0 || lines.length === 0) {
      if (lyricActiveIdx !== -1) setActiveLine(-1);
      return;
    }
    let idx = -1;
    for (let i = 0; i < lines.length; i++) {
      if (lines[i]!.start_s <= t + 0.02) idx = i;
      else break;
    }
    if (idx === lyricActiveIdx) return;
    setActiveLine(idx);
    if (idx >= 0) scrollLineIntoView(idx, instant);
  }

  /** Paint past / active / upcoming states across the whole stack. */
  function setActiveLine(idx: number): void {
    lyricActiveIdx = idx;
    for (let i = 0; i < lyricEls.length; i++) {
      const state = idx < 0 ? 'up' : i === idx ? 'active' : i < idx ? 'past' : 'up';
      lyricEls[i]!.dataset.state = state;
    }
  }

  /** Center the active line inside its own scroller — never the page. */
  function scrollLineIntoView(idx: number, instant: boolean): void {
    const box = lyricScrollEl;
    const el = lyricEls[idx];
    if (!box || !el) return;
    const boxRect = box.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const delta = elRect.top - boxRect.top - (box.clientHeight / 2 - el.clientHeight / 2);
    box.scrollTo({
      top: box.scrollTop + delta,
      behavior: instant || prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }

  /* ------------------------------------------------------- the master editor */

  /**
   * Brad's editor. A big, plain, roomy textarea over the master words. On save
   * the Worker re-aligns every transcribed take, so we re-pull the open take's
   * lines and the synced view re-maps itself.
   */
  async function openLyricEditor(): Promise<void> {
    let initial = lyricsMaster ?? '';
    if (!lyricsMasterLoaded) {
      try {
        const r = await getSongLyrics(songId, abort.signal);
        if (abort.signal.aborted) return;
        lyricsMaster = r.text;
        lyricsMasterLoaded = true;
        initial = r.text ?? '';
      } catch {
        initial = lyricsMaster ?? '';
      }
    }

    const root = document.getElementById('overlay')!;
    const scrim = h('div', { class: 'scrim' });
    const panel = h('div', { class: 'sheet lyr-editor-sheet', role: 'dialog', 'aria-label': 'Edit lyrics' });

    const ta = h('textarea', {
      class: 'lyr-editor',
      placeholder: 'Type the words the way you sing them —\none line at a time.',
      'aria-label': 'The master lyrics',
      spellcheck: 'true',
    }) as HTMLTextAreaElement;
    ta.value = initial;

    const save = h('button', { class: 'btn-sm primary', text: 'Save' });
    const cancel = h('button', { class: 'btn-sm', text: 'Cancel' });

    const close = (): void => {
      scrim.remove();
      panel.remove();
      document.removeEventListener('keydown', onKey);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') close();
    };

    save.addEventListener('click', () => {
      const text = ta.value.trim();
      close();
      void saveLyrics(text);
    });
    cancel.addEventListener('click', close);
    scrim.addEventListener('click', close);
    document.addEventListener('keydown', onKey);

    panel.append(
      h('div', { class: 'grab' }),
      h('h3', { text: 'Lyrics' }),
      h('p', {
        class: 'lyr-editor-hint',
        text: 'The clean master words. The app times them to each take by itself.',
      }),
      ta,
      h('div', { class: 'sheet-actions' }, [cancel, save]),
    );

    root.append(scrim, panel);
    setTimeout(() => ta.focus(), 60);
  }

  /** Save the master words. Optimistic on the messaging; re-maps on success. */
  async function saveLyrics(text: string): Promise<void> {
    const prev = lyricsMaster;
    lyricsMaster = text || null;
    lyricsMasterLoaded = true;
    // Reflect "words exist now" in the empty-state copy straight away.
    if (lyricsData && lyricsData.lines.length === 0) {
      lyricsData = { ...lyricsData, has_master: !!(lyricsMaster && lyricsMaster.trim()) };
      paintLyricBody();
    }
    try {
      await putSongLyrics(songId, text);
      toast('Saved. Timing the words to this take…');
      // The Worker re-aligns; pull the fresh synced lines for the open take.
      await loadLyrics(sel()?.take_id ?? null);
    } catch (e) {
      lyricsMaster = prev;
      if (lyricsData && lyricsData.lines.length === 0) {
        lyricsData = { ...lyricsData, has_master: !!(lyricsMaster && lyricsMaster.trim()) };
        paintLyricBody();
      }
      toast(e instanceof ApiError ? e.human : "That didn't save. Try again?", true);
    }
  }

  /** Admin: transcribe this take, then reload its lines. */
  async function runTranscribe(): Promise<void> {
    const take = sel()?.take_id;
    if (!take) return;
    lyricsTranscribing = true;
    paintLyricBody();
    try {
      await transcribeTake(take);
      lyricsTranscribing = false;
      await loadLyrics(take);
      toast('Got the words. Play it to follow along.');
    } catch (e) {
      lyricsTranscribing = false;
      paintLyricBody();
      toast(e instanceof ApiError ? e.human : "Couldn't transcribe that one. Try again?", true);
    }
  }

  /* --------------------------------------------------------------- search */

  function buildLyricSearch(): HTMLElement {
    const input = h('input', {
      type: 'search',
      class: 'lyr-searchinput',
      placeholder: 'Search a line — “harbour”…',
      'aria-label': 'Search lyrics across every song',
      enterkeyhint: 'search',
      autocomplete: 'off',
    }) as HTMLInputElement;
    input.addEventListener('input', () => onSearchInput(input.value));

    const bar = h('div', { class: 'lyr-searchbar' }, [
      h('span', { class: 'lyr-searchic', html: icon.search, 'aria-hidden': 'true' }),
      input,
    ]);

    const results = h('ul', { class: 'lyr-results' });
    lyricResultsEl = results;
    return h('div', { class: 'lyr-search' }, [bar, results]);
  }

  function onSearchInput(raw: string): void {
    clearTimeout(searchTimer);
    searchTimer = window.setTimeout(() => void runSearch(raw), 220);
  }

  async function runSearch(raw: string): Promise<void> {
    const query = raw.trim();
    if (query.length < 2) {
      renderResults([], query);
      return;
    }
    searchAbort?.abort();
    searchAbort = new AbortController();
    try {
      const hits = await searchLyrics(query, searchAbort.signal);
      renderResults(hits, query);
    } catch (e) {
      if ((e as Error)?.name === 'AbortError') return;
      // A search that fails is a quiet non-event — never a red box over the words.
      renderResults([], query);
    }
  }

  function renderResults(hits: LyricHit[], query: string): void {
    const box = lyricResultsEl;
    if (!box) return;
    clear(box);
    if (hits.length === 0) {
      if (query.length >= 2) {
        box.appendChild(h('li', { class: 'lyr-noresults', text: 'Nothing with those words — yet.' }));
      }
      return;
    }
    for (const hit of hits.slice(0, 12)) {
      const btn = h('button', {
        class: 'lyr-result',
        'aria-label': `“${hit.text}” — ${hit.song_title} at ${fmtTime(hit.start_s)}`,
      }, [
        lyricSnippet(hit.text, query),
        h('span', { class: 'lyr-result-meta', text: `— ${hit.song_title} @ ${fmtTime(hit.start_s)}` }),
      ]);
      btn.addEventListener('click', () => goToHit(hit));
      box.appendChild(h('li', {}, [btn]));
    }
  }

  /** A windowed, match-highlighted snippet: ‘…the harbour lights…’. */
  function lyricSnippet(text: string, query: string): HTMLElement {
    const el = h('span', { class: 'lyr-result-snippet' });
    const at = text.toLowerCase().indexOf(query.toLowerCase());
    if (at < 0) {
      el.appendChild(document.createTextNode(`‘${text}’`));
      return el;
    }
    const pad = 18;
    const from = Math.max(0, at - pad);
    const to = Math.min(text.length, at + query.length + pad);
    const pre = (from > 0 ? '‘…' : '‘') + text.slice(from, at);
    const mid = text.slice(at, at + query.length);
    const post = text.slice(at + query.length, to) + (to < text.length ? '…’' : '’');
    el.append(
      document.createTextNode(pre),
      h('span', { class: 'lyr-result-mark', text: mid }),
      document.createTextNode(post),
    );
    return el;
  }

  /** Navigate to the hit's song and seek its take. Same-song jumps apply now. */
  function goToHit(hit: LyricHit): void {
    pendingLyricSeek = { songId: hit.song_id, takeId: hit.take_id, start_s: hit.start_s };
    if (hit.song_id === songId) {
      applyLyricSeek(); // already here — no navigation, keep the tap gesture live
    } else {
      location.hash = '#/song/' + encodeURIComponent(hit.song_id);
    }
  }

  /**
   * Consume a pending seek intent for THIS song: select the version whose take
   * matches, play from the line, and bring the words into view. Called on mount
   * (after load) and directly for a same-song jump.
   */
  function applyLyricSeek(): void {
    const intent = pendingLyricSeek;
    if (!intent || intent.songId !== songId || !state.data) return;
    pendingLyricSeek = null;

    const v =
      state.data.versions.find((x) => x.take_id === intent.takeId) ?? sel();
    if (!v) return;

    const wasSelected = state.selected === v.id;
    state.selected = v.id;
    if (!wasSelected) render(); // repaint selection + reload this take's lines

    void playVersion(v, intent.start_s);
    lyricsHost.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'start',
    });
  }

  /* ==================================================================== load */

  async function load(): Promise<void> {
    state.error = null;
    render();
    try {
      const data = await getSong(songId, abort.signal);
      state.data = data;
      state.selected = data.song.canonical?.version_id ?? data.versions[0]?.id ?? null;
    } catch (e) {
      if (abort.signal.aborted) return;
      state.error = e instanceof ApiError ? e.human : 'Couldn’t reach the vault. Try again?';
      render();
      return;
    }
    render();
    void loadWave();
    void loadDaw();
    void loadMembers();
    // A lyric-search hit that navigated here waits for the data — now select its
    // take, seek to the matched line, and scroll the words into view.
    if (pendingLyricSeek) applyLyricSeek();
  }

  /**
   * Fetch the peaks JSON and the Tier-1 mix presets for the selected version.
   * Both are allowed to be missing: peaks show up when the Roadie has run, and
   * a take that nobody has rendered alternate mixes for simply has none. The
   * screen works either way, and it says so in words rather than spinning.
   */
  async function loadWave(): Promise<void> {
    const v = sel();
    if (!v) return;

    state.peaks = null;
    state.duration = v.duration_s ?? 0;
    state.peaksPending = true;
    renderWave();

    let peaksUrl: string | null = null;
    try {
      const info = await player.streamInfo(v.id);
      peaksUrl = info.peaks_url;
      if (info.duration_s) state.duration = info.duration_s;
    } catch {
      // No stream URL means the take isn't ready. Playback will say so if they
      // press play; the waveform just stays a friendly blank.
    }

    if (abort.signal.aborted) return;

    if (peaksUrl) {
      state.peaks = await getPeaks(peaksUrl, abort.signal);
    }
    state.peaksPending = false;

    if (abort.signal.aborted) return;
    renderWave();

    // The mix presets ("turn the drums off", Tier 1) and the live layer mixer
    // (Tier 2) are both owned by the mounted mixer now — see syncMixer(). It
    // fetches its own /mix and gates itself, so there's nothing to fetch here.
  }

  /* ================================================================== render */

  function render(): void {
    clear(body);

    if (state.error) {
      const retry = h('button', { class: 'btn-sm primary', text: 'Try again' });
      retry.addEventListener('click', () => void load());
      body.appendChild(emptyState("Couldn't reach the vault", state.error, retry));
      return;
    }

    if (!state.data) {
      body.appendChild(skeleton(2));
      return;
    }

    const { song, versions, comments } = state.data;
    titleEl.textContent = song.title;

    /* ---------------------------------------------------------- the hero */

    vinyl?.destroy();
    vinyl = createVinyl({
      color: song.color || '#d8241a',
      arm: true,
      getTime: () => ({ time: player.get().currentTime, duration: player.get().duration }),
      // Drag the record to scrub. Only meaningful if this is what's playing.
      onScrub: (t) => {
        if (player.isCurrent(state.selected ?? '')) player.seek(t);
      },
    });

    const v = sel();

    const hero = h('div', { class: 'song-hero' }, [
      vinyl.el,
      h('h1', { text: song.title }),
      h('p', {
        class: 'whichone',
        text: v ? whichOne(song.canonical?.version_id === v.id, v) : "Nobody's picked the best one yet",
      }),
    ]);

    body.appendChild(hero);

    /* ------------------------------------------------------- the waveform */

    body.appendChild(waveWrap);
    body.appendChild(transport());
    body.appendChild(times);

    // A red "+" right under the wave — drag the playhead to the moment, tap this
    // to pin a note there. This is J6, the reason these recordings exist ("that
    // ending at 2:41"), made a one-tap thing instead of a hidden press-and-hold.
    const waveAdd = h('button', {
      class: 'wave-addnote',
      'aria-label': 'Note this moment (at the playhead)',
    });
    waveAdd.append(
      h('span', { class: 'wave-addnote-plus', text: '+' }),
      h('span', { class: 'wave-addnote-label', text: 'note this moment' }),
    );
    waveAdd.addEventListener('click', () => void noteAt(player.get().currentTime));
    body.appendChild(h('div', { class: 'wave-addnote-row' }, [waveAdd]));

    /* --------------------------------------------------------- the mixes */
    // The mixer draws its own heading and radio group when there's more than one
    // preset, and its own "open the layer mixer" when a take has aligned layers.
    // We just place its stable host and make sure it's mounted for this take.
    body.appendChild(mixHost);
    syncMixer();

    /* --------------------------------------------------------- the lyrics */
    // Right under the player, so the words scroll along as the take plays. Its
    // own stable host — a vote or a note below never disturbs a scroll in flight.
    body.appendChild(lyricsHost);
    renderLyrics();

    /* ------------------------------------------------------ the versions */

    body.appendChild(
      h('div', { class: 'section-h' }, [
        h('h2', { text: versions.length === 1 ? 'The take' : 'The takes' }),
      ]),
    );

    if (versions.length === 0) {
      body.appendChild(
        emptyState(
          'No takes yet',
          "There's nothing recorded against this one. Hit + and put something down.",
        ),
      );
    } else {
      const ul = h('ul', { class: 'versions' });
      for (const ver of versions) ul.appendChild(versionRow(ver, song.canonical?.version_id === ver.id));
      body.appendChild(ul);
    }

    /* --------------------------------------------- who's heard this (accountability) */
    // Its own stable host, between the takes and the notes: you see what's here,
    // then who's caught up, then the notes that ask "has everyone listened?".
    body.appendChild(heardHost);
    renderHeard();

    /* ------------------------------------------------------- the notes (J6) */

    // Name the take these notes belong to, so it's unmistakable that notes are
    // per-take: switch takes above and this heading + the list below both change.
    const selName = sel()?.name;
    body.appendChild(
      h('div', { class: 'section-h' }, [
        h('h2', { text: 'Notes' }),
        selName ? h('span', { class: 'section-sub', text: `on ${selName}` }) : null,
      ]),
    );

    const mine = comments.filter((c) => c.version_id === state.selected);
    const live = mine.filter((c) => !c.done);
    const doneNotes = mine.filter((c) => c.done);

    // Publish this take's note timestamps so the footer wave shows the same dots.
    if (state.selected) {
      setNoteMarks(
        state.selected,
        live.filter((c) => c.at_s != null).map((c) => c.at_s as number),
      );
    }

    if (live.length === 0) {
      body.appendChild(
        emptyState(
          'No notes yet',
          'Play to the moment, then tap the red "note this moment" to pin it — "that ending at 2:41" is the whole reason these recordings exist. Or leave a note about the whole song below.',
        ),
      );
    } else {
      const ul = h('ul', { class: 'comments' });
      for (const c of live) ul.appendChild(commentRow(c));
      body.appendChild(ul);
    }

    // The quiet second way in: a note about the take/song, with no timestamp and
    // no pin. Labelled in plain words next to the red "note this moment" so the
    // difference is obvious without anyone having to think about it.
    const genAdd = h('button', { class: 'note-add-general', 'aria-label': 'Add a note about the whole song' }, [
      h('span', { class: 'note-add-plus', text: '+' }),
      h('span', { text: 'note about the song' }),
    ]);
    genAdd.addEventListener('click', () => void noteAt(null));
    body.appendChild(genAdd);

    // Done notes are tucked away by default so the list stays the live to-do.
    // A quiet toggle brings them back (dimmed, struck through) so they can be
    // un-ticked. No pins for them either — a done note has left the wave.
    if (doneNotes.length > 0) {
      const toggle = h('button', {
        class: 'note-donetoggle',
        'aria-expanded': state.showDone ? 'true' : 'false',
        text: state.showDone ? 'Hide done' : `Show done (${doneNotes.length})`,
      });
      toggle.addEventListener('click', () => {
        state.showDone = !state.showDone;
        render();
      });
      body.appendChild(toggle);

      if (state.showDone) {
        const dul = h('ul', { class: 'comments note-donelist' });
        for (const c of doneNotes) dul.appendChild(commentRow(c));
        body.appendChild(dul);
      }
    }

    /* ---------------------------------------------------------- the studio */
    // Its own clearly-separated block, after the takes and the notes. A stable
    // host the studio manages itself, so votes/notes never disturb an upload.
    body.appendChild(studioHost);
    renderStudio();

    renderWave();
    paintPlaying();
  }

  function whichOne(isCanonical: boolean, v: Version): string {
    const when = v.recorded_at ? fmtDate(v.recorded_at) : '';
    const who = v.created_by?.name;
    const parts = [when, who].filter(Boolean).join(' · ');
    if (isCanonical) return parts ? `The one everyone agreed on · ${parts}` : 'The one everyone agreed on';
    return parts;
  }

  /* ---------------------------------------------------------- the waveform */

  const waveHost = h('div', { class: 'wave-host' });
  const pinLayer = h('div', { class: 'note-pins' });
  // The live-comment overlay — notes float up over the wave as the take plays.
  // Its own layer so a re-render (a vote, a fresh note) never clears a note
  // that's mid-flight; renderWave() only clears waveHost + pinLayer.
  const liveLayer = h('div', { class: 'livenote-layer' });
  const waveWrap = h('div', { class: 'wave-wrap card' }, [waveHost, pinLayer, liveLayer]);

  /** The notes currently floating in the live overlay, by comment id. */
  const liveShown = new Map<string, { el: HTMLElement; leaving: boolean; timer: number }>();
  const times = h('div', { class: 'times' }, [
    h('span', { class: 'at', text: '0:00' }),
    h('span', { class: 'of', text: '0:00' }),
  ]);

  function renderWave(): void {
    ws?.destroy(); // safe: with external media, destroy() doesn't touch the element
    ws = null;
    clear(waveHost);
    clear(pinLayer);

    if (state.peaksPending) {
      waveHost.appendChild(h('div', { class: 'wave-empty', text: 'Drawing the wave…' }));
      return;
    }

    if (!state.peaks || !(state.duration > 0)) {
      // No peaks. This is normal and temporary — the Roadie hasn't run yet.
      // It is NOT a reason to block playback, and it is NOT an error.
      waveHost.appendChild(
        h('div', {
          class: 'wave-empty',
          text: "The picture of this one isn't ready yet — but it plays just fine.",
        }),
      );
      return;
    }

    const current = player.isCurrent(state.selected ?? '');
    const dim = isLight() ? '#c8c8cf' : '#3a3a3a';

    ws = WaveSurfer.create({
      container: waveHost,
      // The shared element. wavesurfer draws; it does not own playback.
      media: player.element(),
      // Pre-computed on the Mac. NO `url` — `url` is what makes it fetch+decode.
      peaks: state.peaks,
      duration: state.duration,

      height: 84,
      waveColor: dim,
      // If this version isn't what's playing, the media element's currentTime
      // belongs to some other song — so we hide the progress and the cursor
      // rather than let them tell a lie.
      progressColor: current ? '#d8241a' : dim,
      cursorColor: '#d8241a',
      // A fat, grabbable playhead when this is the one playing — you drag it to
      // scrub. Hidden on a version that isn't playing (its time would be a lie).
      cursorWidth: current ? 3 : 0,
      barWidth: 2,
      barGap: 1,
      barRadius: 2,
      normalize: true,
      interact: current, // when it's not ours, we handle taps ourselves (below)
      dragToSeek: current, // drag the playhead to scrub
    });

    // Mark the host so the CSS can hang a draggable knob on the red cursor and
    // switch the cursor to a grab hand — the playhead reads as a handle.
    waveHost.classList.toggle('is-draggable', current);

    if (!current) {
      waveHost.addEventListener('click', onWaveTap);
    }

    renderPins();
  }

  function timeAtX(clientX: number): number {
    const r = waveHost.getBoundingClientRect();
    const p = Math.min(1, Math.max(0, (clientX - r.left) / r.width));
    return p * state.duration;
  }

  /** Tapping the wave of a version that isn't playing starts it, right there. */
  function onWaveTap(e: MouseEvent): void {
    const v = sel();
    if (!v) return;
    void playVersion(v, timeAtX(e.clientX));
  }

  const snapSec = (t: number): number => Math.round(t);
  const clampSec = (t: number, lo: number, hi: number): number => Math.min(Math.max(t, lo), hi);
  const clampPct = (p: number): number => Math.min(100, Math.max(0, p));

  /**
   * The markers on the wave where a note lives. Two shapes:
   *  · a MOMENT note (at_s, no end_s) → a fat red pin you can thumb, with a
   *    dashed grip to its right you drag to stretch it into a part;
   *  · a DURATION note (at_s + end_s) → a translucent red band spanning the
   *    part, carrying the note's words, draggable to move and with a round
   *    grip on its trailing edge to change where it ends.
   * A general note (no timestamp) has no moment to sit on, so it gets no mark;
   * a note that's been ticked done leaves the wave.
   */
  function renderPins(): void {
    clear(pinLayer);
    if (!state.data || !(state.duration > 0)) return;
    const d = state.duration;

    for (const c of state.data.comments) {
      if (c.version_id !== state.selected || c.at_s == null || c.done) continue;
      if (c.end_s != null && c.end_s > c.at_s) addZoneMark(c, d);
      else addPointMark(c, d);
    }
  }

  /** A moment pin + its "pull me into a part" grip. */
  function addPointMark(c: Comment, d: number): void {
    const pct = clampPct((c.at_s! / d) * 100);
    const pin = h('button', {
      class: 'note-pin',
      'aria-label': `Note at ${fmtTime(c.at_s!)}${c.body ? ` — ${c.body}` : ''}`,
      title: c.body,
    });
    pin.style.left = `${pct}%`;
    pin.addEventListener('click', (e) => {
      e.stopPropagation();
      openNoteMenu(c);
    });

    const grip = h('button', {
      class: 'zone-grip is-point-grip',
      'aria-label': 'Drag to stretch this note over a part of the song',
    });
    grip.style.left = `${pct}%`;
    attachEndGrip(grip, c);

    pinLayer.append(pin, grip);
  }

  /** A duration band: the words, a move-handle (the band itself), an end grip. */
  function addZoneMark(c: Comment, d: number): void {
    const a = c.at_s!;
    const b = c.end_s!;
    const band = h('div', {
      class: 'zone-band',
      role: 'button',
      tabindex: '0',
      title: c.body,
      'aria-label': `Note from ${fmtTime(a)} to ${fmtTime(b)}${c.body ? ` — ${c.body}` : ''}`,
    });
    band.style.left = `${clampPct((a / d) * 100)}%`;
    band.style.width = `${clampPct(((b - a) / d) * 100)}%`;

    const label = h('span', { class: 'zone-label', text: c.body });
    const grip = h('button', { class: 'zone-grip', 'aria-label': 'Drag to change where this note ends' });
    band.append(label, grip);

    band.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openNoteMenu(c);
      }
    });
    attachZoneMove(band, c);
    attachEndGrip(grip, c);

    pinLayer.appendChild(band);
  }

  /**
   * Drag the trailing grip to set where a note ends. On a moment note it draws a
   * preview band that becomes a real zone on release; on a zone it resizes the
   * band live. Dragged back under a second collapses a zone to a moment again.
   * Pointer events + pointer capture, snapped to whole seconds — forgiving on a
   * thumb, and it never reaches wavesurfer's own seek (the grip owns the pointer).
   */
  function attachEndGrip(grip: HTMLElement, c: Comment): void {
    const band = grip.parentElement?.classList.contains('zone-band')
      ? (grip.parentElement as HTMLElement)
      : null;
    let dragging = false;
    let moved = false;
    let startX = 0;
    let preview: HTMLElement | null = null;

    const paint = (a: number, end: number): void => {
      const d = state.duration;
      if (!(d > 0)) return;
      if (band) {
        band.style.width = `${clampPct(((end - a) / d) * 100)}%`;
      } else {
        if (!preview) {
          preview = h('div', { class: 'zone-band zone-preview' });
          pinLayer.appendChild(preview);
        }
        preview.style.left = `${clampPct((a / d) * 100)}%`;
        preview.style.width = `${clampPct(((end - a) / d) * 100)}%`;
      }
    };

    grip.addEventListener('pointerdown', (e) => {
      if (c.at_s == null || !(state.duration > 0)) return;
      e.preventDefault();
      e.stopPropagation();
      dragging = true;
      moved = false;
      startX = e.clientX;
      try {
        grip.setPointerCapture(e.pointerId);
      } catch {
        /* older browsers without capture — the move still tracks by clientX */
      }
      waveHost.classList.add('is-noteediting');
    });

    grip.addEventListener('pointermove', (e) => {
      if (!dragging || c.at_s == null) return;
      if (!moved && Math.abs(e.clientX - startX) <= 6) return; // ignore a jittery tap
      moved = true;
      paint(c.at_s, clampSec(snapSec(timeAtX(e.clientX)), c.at_s, state.duration));
    });

    const finish = (e: PointerEvent): void => {
      if (!dragging || c.at_s == null) return;
      dragging = false;
      try {
        grip.releasePointerCapture(e.pointerId);
      } catch {
        /* nothing to release */
      }
      waveHost.classList.remove('is-noteediting');
      preview?.remove();
      preview = null;

      // A tap (no real travel) is not a resize — a moment's grip opens its menu,
      // a zone's grip just leaves it be. Only a real drag changes the span.
      if (!moved) {
        renderPins();
        if (!band) openNoteMenu(c);
        return;
      }

      const end = clampSec(snapSec(timeAtX(e.clientX)), c.at_s, state.duration);
      if (end - c.at_s >= MIN_ZONE_S) void setNoteSpan(c, c.at_s, end);
      else if (c.end_s != null) void setNoteSpan(c, c.at_s, null); // shrunk to nothing → a moment again
      else renderPins(); // no real change; just redraw the mark
    };
    grip.addEventListener('pointerup', finish);
    grip.addEventListener('pointercancel', finish);
  }

  /** Drag the whole band to slide a part earlier/later, keeping its length. A
   *  press that doesn't move is a tap — it opens the note's menu. */
  function attachZoneMove(band: HTMLElement, c: Comment): void {
    let dragging = false;
    let moved = false;
    let grabOffset = 0;
    let span = 0;

    band.addEventListener('pointerdown', (e) => {
      if (c.at_s == null || c.end_s == null || !(state.duration > 0)) return;
      e.stopPropagation();
      dragging = true;
      moved = false;
      span = c.end_s - c.at_s;
      grabOffset = timeAtX(e.clientX) - c.at_s;
      try {
        band.setPointerCapture(e.pointerId);
      } catch {
        /* fine without capture */
      }
      waveHost.classList.add('is-noteediting');
    });

    band.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      const start = clampSec(snapSec(timeAtX(e.clientX) - grabOffset), 0, Math.max(0, state.duration - span));
      if (Math.abs(start - c.at_s!) > 0.001) moved = true;
      band.style.left = `${clampPct((start / state.duration) * 100)}%`;
    });

    const finish = (e: PointerEvent): void => {
      if (!dragging) return;
      dragging = false;
      try {
        band.releasePointerCapture(e.pointerId);
      } catch {
        /* nothing to release */
      }
      waveHost.classList.remove('is-noteediting');
      if (!moved) {
        openNoteMenu(c);
        return;
      }
      const start = clampSec(snapSec(timeAtX(e.clientX) - grabOffset), 0, Math.max(0, state.duration - span));
      void setNoteSpan(c, start, start + span);
    };
    band.addEventListener('pointerup', finish);
    band.addEventListener('pointercancel', finish);
  }

  /**
   * Set a note's span. Optimistic, like everything else here: the band snaps to
   * its new shape at once and the write reconciles behind it. `end_s: null`
   * collapses a zone back to a single moment.
   */
  async function setNoteSpan(c: Comment, at_s: number, end_s: number | null): Promise<void> {
    if (c.at_s === at_s && (c.end_s ?? null) === (end_s ?? null)) {
      renderPins();
      return;
    }
    const oldAt = c.at_s;
    const oldEnd = c.end_s ?? null;
    c.at_s = at_s;
    c.end_s = end_s;
    sortComments();
    render();
    toast(end_s != null ? `That part — ${fmtTime(at_s)}–${fmtTime(end_s)}.` : `Back to one moment, ${fmtTime(at_s)}.`);
    try {
      await editComment(c.id, { at_s, end_s });
    } catch (e) {
      c.at_s = oldAt;
      c.end_s = oldEnd;
      sortComments();
      render();
      toast(e instanceof ApiError ? e.human : "That didn't save. Try again?", true);
    }
  }

  /* ---------------------------------------------------------- the transport */

  const bigPlay = h('button', { class: 'btn-play play-circle', 'aria-label': 'Play' });

  function transport(): HTMLElement {
    bigPlay.addEventListener('click', () => {
      const v = sel();
      if (!v) return;
      if (player.isCurrent(v.id)) player.toggle();
      else void playVersion(v);
    });

    const b15 = h('button', { class: 'iconbtn skip15', text: '−15', 'aria-label': 'Back 15 seconds' });
    const f15 = h('button', { class: 'iconbtn skip15', text: '+15', 'aria-label': 'Forward 15 seconds' });
    b15.addEventListener('click', () => player.seek(player.get().currentTime - 15));
    f15.addEventListener('click', () => player.seek(player.get().currentTime + 15));

    return h('div', { class: 'transport-row' }, [b15, bigPlay, f15]);
  }

  async function playVersion(v: Version, seekTo?: number): Promise<void> {
    const song = state.data!.song;
    // Switching takes — clear any notes still floating from the old one.
    const switched = state.selected !== v.id;
    if (switched) clearLiveNotes();
    state.selected = v.id;

    await player.play({
      versionId: v.id,
      songId: song.id,
      takeId: v.take_id,
      title: song.title,
      subtitle: v.name || fmtDate(v.recorded_at),
      color: song.color || '#d8241a',
      seekTo,
    });

    const err = player.get().error;
    if (err) toast(err, true);

    if (switched) {
      // A different take is a different set of notes, a different waveform, and
      // maybe different words. Repaint the lists (notes filter to this take) and
      // reload THIS take's peaks — mirroring what load() does on first open.
      render();
      void loadWave();
    } else {
      // Same take, just (re)started — the light path.
      renderWave();
      paintPlaying();
      renderLyrics();
    }
  }

  /* ------------------------------------------------------- votes: who voted */

  /** My own vote reads as "You"; everyone else by their real name. */
  function voterName(vt: Voter): string {
    return me && vt.member_id === me.id ? 'You' : vt.name || 'Someone';
  }

  /** "Rob" · "You and Rob" · "Rob, Trev and Brad" — with You always first. */
  function nameList(names: string[]): string {
    const ns = [...names.filter((n) => n === 'You'), ...names.filter((n) => n !== 'You')];
    if (ns.length <= 1) return ns[0] ?? '';
    if (ns.length === 2) return `${ns[0]} and ${ns[1]}`;
    return `${ns.slice(0, -1).join(', ')} and ${ns[ns.length - 1]}`;
  }

  /** Singular verb only for one *other* person; "You" always takes the plural. */
  function areVerb(names: string[], singular: string, plural: string): string {
    return names.length === 1 && names[0] !== 'You' ? singular : plural;
  }

  /**
   * The quiet who-voted line under a take — "Rob, Trev 👍 · Brad 👎" in bone and
   * red, from version.voters. The icons are decorative, so the whole line
   * carries a spoken label: "Loved by Rob, Trev. Not feeling it: Brad."
   */
  function votersLine(voters: Voter[] | undefined): HTMLElement | null {
    const list = voters ?? [];
    if (!list.length) return null;
    const ups = list.filter((v) => v.value === 1).map(voterName);
    const downs = list.filter((v) => v.value === -1).map(voterName);

    const grp = (dir: 'up' | 'down', names: string[]): HTMLElement =>
      h('span', { class: 'vote-voters-grp' }, [
        h('span', { class: 'vote-voters-names', text: nameList(names) }),
        h('span', {
          class: `vote-voters-ic ${dir}`,
          html: dir === 'up' ? icon.up : icon.down,
          'aria-hidden': 'true',
        }),
      ]);

    const line = h('div', { class: 'vote-voters' });
    if (ups.length) line.append(grp('up', ups));
    if (ups.length && downs.length) line.append(h('span', { class: 'vote-voters-mid', text: '·' }));
    if (downs.length) line.append(grp('down', downs));

    const say: string[] = [];
    if (ups.length) say.push(`Loved by ${nameList(ups)}`);
    if (downs.length) say.push(`Not feeling it: ${nameList(downs)}`);
    line.setAttribute('aria-label', say.join('. '));
    return line;
  }

  /**
   * Move MY thumb on a votes+voters pair, in place: fix the up/down tally, set
   * `mine`, and add/replace/remove me in the voter list. Shared by takes and
   * notes so the optimistic update and its revert stay identical.
   */
  function applyMyVote(votes: Votes, voters: Voter[], value: Vote): void {
    const before = votes.mine ?? 0;
    if (before === 1) votes.up = Math.max(0, votes.up - 1);
    if (before === -1) votes.down = Math.max(0, votes.down - 1);
    if (value === 1) votes.up++;
    if (value === -1) votes.down++;
    votes.mine = value;

    // No identity resolved yet: the tally still moved; the names reconcile on
    // the next real load. With one, keep my entry in the voter list truthful.
    if (!me) return;
    const idx = voters.findIndex((x) => x.member_id === me!.id);
    if (idx >= 0) voters.splice(idx, 1);
    if (value !== 0) voters.push({ member_id: me.id, name: me.name, value });
  }

  /* -------------------------------------------------------- the versions */

  function versionRow(v: Version, isCanonical: boolean): HTMLElement {
    const row = h('li', {
      class: 'version card',
      'data-canonical': isCanonical ? '1' : '0',
      'data-current': player.isCurrent(v.id) ? '1' : '0',
      'data-selected': state.selected === v.id ? '1' : '0',
      'data-version': v.id,
    });

    // The star lives on the LEFT edge and IS the "main version" switch: filled
    // and red on the canonical take, a hollow tappable star on the rest. One tap
    // promotes a take to the one on the shelf — no digging through the ⋯ menu.
    const star = h('button', {
      class: 'version-star',
      'data-on': isCanonical ? '1' : '0',
      html: icon.star,
      'aria-pressed': isCanonical ? 'true' : 'false',
      'aria-label': isCanonical ? 'This is the main version' : 'Make this the main version',
      title: isCanonical ? 'The main version' : 'Make this the main version',
    });
    star.addEventListener('click', (e) => {
      e.stopPropagation();
      if (isCanonical) toast('This is already the main version.');
      else void makeCanonical(v);
    });

    const name = h('div', { class: 'version-name' }, [
      h('span', { class: 'txt', text: v.name || fmtDate(v.recorded_at) || 'A take' }),
    ]);

    const bits: (Node | string)[] = [];
    if (v.recorded_at) bits.push(h('span', { text: fmtDate(v.recorded_at) }));
    // Where it came from — "zoom" / "phone" — in dim mono, before who added it.
    if (v.device && v.device !== 'mixdown') {
      if (bits.length) bits.push(h('span', { class: 'dot', text: '·' }));
      bits.push(h('span', { class: 'device', text: v.device }));
    }
    if (v.created_by?.name) {
      if (bits.length) bits.push(h('span', { class: 'dot', text: '·' }));
      bits.push(h('span', { text: v.created_by.name }));
    }
    if (v.duration_s) {
      if (bits.length) bits.push(h('span', { class: 'dot', text: '·' }));
      bits.push(h('span', { text: fmtTime(v.duration_s) }));
    }
    // Plays for THIS take alone — what the user asked for. Only when someone has
    // actually heard it, so a fresh take stays uncluttered.
    if (v.play_count && v.play_count > 0) {
      if (bits.length) bits.push(h('span', { class: 'dot', text: '·' }));
      bits.push(h('span', { class: 'version-plays', text: plural(v.play_count, 'play') }));
    }

    const main = h('button', { class: 'version-main', 'aria-label': `Play ${v.name}` }, [
      name,
      h('div', { class: 'version-meta' }, bits),
    ]);
    main.addEventListener('click', () => void playVersion(v));

    /* 👍 / 👎 — straight over from the Recording Hub prototype. */
    const up = h('button', {
      class: 'vote up',
      'aria-pressed': v.votes.mine === 1 ? 'true' : 'false',
      'aria-label': 'Good one',
    }, [h('span', { html: icon.up }), h('span', { text: String(v.votes.up || '') })]);

    const down = h('button', {
      class: 'vote down',
      'aria-pressed': v.votes.mine === -1 ? 'true' : 'false',
      'aria-label': 'Not this one',
    }, [h('span', { html: icon.down }), h('span', { text: String(v.votes.down || '') })]);

    up.addEventListener('click', (e) => {
      e.stopPropagation();
      void castVote(v, v.votes.mine === 1 ? 0 : 1);
    });
    down.addEventListener('click', (e) => {
      e.stopPropagation();
      void castVote(v, v.votes.mine === -1 ? 0 : -1);
    });

    const more = h('button', { class: 'iconbtn', html: icon.more, 'aria-label': 'More' });
    more.addEventListener('click', (e) => {
      e.stopPropagation();
      versionMenu(v, isCanonical);
    });

    row.append(star, main, h('div', { class: 'version-side' }, [up, down, more]));

    // Who thumbed it, quietly, on a full-width line beneath the take. Only drawn
    // when someone actually has — an unvoted take stays clean.
    const voters = votersLine(v.voters);
    if (voters) row.append(voters);

    return row;
  }

  /**
   * Optimistic. The vote lands on screen instantly and reconciles behind the
   * scenes, because a phone in a pub basement takes two seconds to answer and
   * a ♥ that doesn't fill in feels broken.
   */
  async function castVote(v: Version, value: Vote): Promise<void> {
    const beforeVotes = { ...v.votes };
    const beforeVoters = (v.voters ?? []).map((x) => ({ ...x }));
    v.voters ??= [];
    applyMyVote(v.votes, v.voters, value);
    render(); // repaints the thumbs AND the who-voted line under this take

    try {
      await apiVote(v.id, value);
    } catch (e) {
      // Put it back exactly as it was — tally, my thumb, and the names.
      v.votes = beforeVotes;
      v.voters = beforeVoters;
      render();
      toast(e instanceof ApiError ? e.human : "That didn't save. Try again?", true);
    }
  }

  function versionMenu(v: Version, isCanonical: boolean): void {
    const items = [];

    if (!isCanonical) {
      items.push({
        label: 'Make this the one',
        icon: icon.star,
        accent: true,
        onSelect: () => void makeCanonical(v),
      });
    }

    items.push({
      label: 'Edit take',
      icon: icon.pencil,
      onSelect: async () => {
        const res = await formSheet('Edit take', [
          { name: 'name', label: 'Name', value: v.name ?? '', placeholder: 'Apr 4 full band' },
          { name: 'date', label: 'Date recorded', type: 'date', value: toDateInputValue(v.recorded_at) },
          {
            name: 'device',
            label: 'Where / what recorded it',
            value: v.device && v.device !== 'mixdown' ? v.device : '',
            placeholder: 'zoom, phone, jam space…',
            hint: 'The “location” — where it was tracked, or the device.',
          },
        ]);
        if (!res) return;

        const patch: { name?: string; recorded_at?: number | null; device?: string | null } = {};
        const newName = (res.name ?? '').trim();
        if (newName && newName !== v.name) patch.name = newName;
        const newDate = dateInputToUnix(res.date ?? '');
        if ((newDate ?? null) !== (v.recorded_at ?? null)) patch.recorded_at = newDate;
        const newDevice = (res.device ?? '').trim() || null;
        if ((newDevice ?? null) !== ((v.device && v.device !== 'mixdown' ? v.device : null) ?? null)) {
          patch.device = newDevice;
        }
        if (Object.keys(patch).length === 0) return;

        // Optimistic: land the change instantly, roll back if the save fails.
        const before = { name: v.name, recorded_at: v.recorded_at, device: v.device };
        if (patch.name !== undefined) v.name = patch.name;
        if (patch.recorded_at !== undefined) v.recorded_at = patch.recorded_at;
        if (patch.device !== undefined) v.device = patch.device;
        render();
        try {
          await editVersion(v.id, patch);
          toast('Saved.');
        } catch (e) {
          v.name = before.name;
          v.recorded_at = before.recorded_at;
          v.device = before.device;
          render();
          toast(e instanceof ApiError ? e.human : "That didn't save. Try again?", true);
        }
      },
    });

    items.push({
      label: 'Archive',
      icon: icon.archive,
      onSelect: async () => {
        // Nothing is ever deleted. Archiving moves it out of the way; the
        // recording is still there, forever. Say that out loud, so nobody is
        // frightened of the button.
        try {
          await archiveVersion(v.id);
          toast('Tucked away. Nothing was deleted.');
          await load();
        } catch (e) {
          toast(e instanceof ApiError ? e.human : "That didn't save. Try again?", true);
        }
      },
    });

    sheet(v.name || 'This take', items);
  }

  async function makeCanonical(v: Version): Promise<void> {
    const data = state.data;
    if (!data) return;
    const before = data.song.canonical;

    data.song.canonical = {
      version_id: v.id,
      name: v.name,
      duration_s: v.duration_s,
      recorded_at: v.recorded_at,
      votes: v.votes,
    };
    render();

    try {
      await setCanonical(data.song.id, v.id);
      toast('That’s the one.');
    } catch (e) {
      data.song.canonical = before;
      render();
      toast(e instanceof ApiError ? e.human : "That didn't save. Try again?", true);
    }
  }

  /* ------------------------------------------------------------ notes (J6) */

  /**
   * Who left it. The backend sends the real name now; 'Someone' is only ever the
   * client-side fallback for a missing author, and the brief is explicit — show
   * nothing rather than a made-up name. So a note with no real name simply has
   * no by-line, and its avatar stays a blank hairline ring.
   */
  function whoName(c: Comment): string | null {
    const n = c.member?.name?.trim();
    if (!n || n.toLowerCase() === 'someone') return null;
    return n;
  }

  /** Notes read back in song order; undated general notes fall to the bottom. */
  function sortComments(): void {
    state.data?.comments.sort((a, b) => {
      if (a.at_s == null && b.at_s == null) return b.created_at - a.created_at;
      if (a.at_s == null) return 1;
      if (b.at_s == null) return -1;
      return a.at_s - b.at_s;
    });
  }

  /** "Trev agrees" · "You and Rob agree · Brad disagrees" — a note's reactions
   *  in plain words, from comment.voters. Empty string when nobody has reacted. */
  function reactionWords(c: Comment): string {
    const list = c.voters ?? [];
    if (!list.length) return '';
    const ups = list.filter((v) => v.value === 1).map(voterName);
    const downs = list.filter((v) => v.value === -1).map(voterName);
    const parts: string[] = [];
    if (ups.length) parts.push(`${nameList(ups)} ${areVerb(ups, 'agrees', 'agree')}`);
    if (downs.length) parts.push(`${nameList(downs)} ${areVerb(downs, 'disagrees', 'disagree')}`);
    return parts.join(' · ');
  }

  /**
   * The reactions row for one note — thumbs + who, on its own line UNDER the note.
   * It sits OUTSIDE the note's own button (a button can't contain buttons), so a
   * thumb reacts without opening the note menu.
   */
  function noteReactions(c: Comment): HTMLElement {
    const votes = c.votes ?? { up: 0, down: 0, mine: 0 };

    const up = h('button', {
      class: 'vote-note up',
      'aria-pressed': votes.mine === 1 ? 'true' : 'false',
      'aria-label': 'Good note',
    }, [h('span', { html: icon.up }), h('span', { text: String(votes.up || '') })]);

    const down = h('button', {
      class: 'vote-note down',
      'aria-pressed': votes.mine === -1 ? 'true' : 'false',
      'aria-label': 'Not sure about this note',
    }, [h('span', { html: icon.down }), h('span', { text: String(votes.down || '') })]);

    up.addEventListener('click', (e) => {
      e.stopPropagation();
      void castNoteVote(c, votes.mine === 1 ? 0 : 1);
    });
    down.addEventListener('click', (e) => {
      e.stopPropagation();
      void castNoteVote(c, votes.mine === -1 ? 0 : -1);
    });

    const who = h('span', { class: 'vote-who', text: reactionWords(c) });
    return h('div', { class: 'vote-note-row' }, [up, down, who]);
  }

  /** Optimistic note reaction — mirrors castVote, reverts on failure. */
  async function castNoteVote(c: Comment, value: Vote): Promise<void> {
    const beforeVotes: Votes = c.votes ? { ...c.votes } : { up: 0, down: 0, mine: 0 };
    const beforeVoters = (c.voters ?? []).map((x) => ({ ...x }));
    c.votes ??= { up: 0, down: 0, mine: 0 };
    c.voters ??= [];
    applyMyVote(c.votes, c.voters, value);
    render();

    try {
      await voteComment(c.id, value);
    } catch (e) {
      c.votes = beforeVotes;
      c.voters = beforeVoters;
      render();
      toast(e instanceof ApiError ? e.human : "That didn't save. Try again?", true);
    }
  }

  function commentRow(c: Comment): HTMLElement {
    const who = whoName(c);
    const row = h('li', { class: 'note-row' }, []);
    const btn = h('button', {
      class: 'comment note-comment',
      'data-done': c.done ? '1' : '0',
      'aria-label': who ? `Note from ${who}` : 'Note',
    }, [
      h('span', { class: 'avatar', text: who ? initials(who) : '' }),
      h('div', {}, [
        h('div', { class: 'comment-head' }, [
          who ? h('span', { class: 'comment-who', text: who }) : null,
          c.at_s != null
            ? h('span', {
                class: 'comment-at',
                text:
                  c.end_s != null && c.end_s > c.at_s
                    ? `${fmtTime(c.at_s)}–${fmtTime(c.end_s)}`
                    : fmtTime(c.at_s),
              })
            : null,
        ]),
        h('div', { class: 'comment-body', text: c.body }),
        h('div', { class: 'comment-when', text: relDate(c.created_at) }),
      ]),
      h('span', { class: 'note-comment-chev', html: icon.more, 'aria-hidden': 'true' }),
    ]);

    // Tap a note → its little menu (go there, edit, move, done, delete). The pin
    // on the wave opens the very same menu.
    btn.addEventListener('click', () => openNoteMenu(c));

    row.appendChild(btn);
    // Its thumbs + who-agrees line, a sibling of the note button (not nested).
    row.appendChild(noteReactions(c));
    return row;
  }

  /**
   * The one place every per-note action lives, in plain words. Opened by tapping
   * the note's row OR its pin on the wave. What's offered depends on the note:
   * a moment-note can go-to and move; a general note can be pinned to a moment;
   * a done note can only be brought back or deleted.
   */
  function openNoteMenu(c: Comment): void {
    const stamped = c.at_s != null;
    const isZone = stamped && c.end_s != null && c.end_s > c.at_s!;
    const items: SheetItem[] = [];

    if (c.done) {
      items.push({
        label: 'Bring it back',
        icon: noteIcon.back,
        accent: true,
        onSelect: () => void setNoteDone(c, false),
      });
      items.push({ label: 'Edit the words', icon: noteIcon.edit, onSelect: () => void editNote(c) });
      items.push({ label: 'Delete', icon: noteIcon.del, onSelect: () => confirmDelete(c) });
    } else {
      if (stamped) {
        items.push({
          label: isZone ? 'Play this part' : 'Go to this moment',
          icon: noteIcon.goto,
          accent: true,
          onSelect: () => goToNote(c),
        });
      }
      items.push({ label: 'Edit the words', icon: noteIcon.edit, onSelect: () => void editNote(c) });
      items.push({
        label: stamped ? 'Move it' : 'Pin it to a moment',
        icon: noteIcon.move,
        onSelect: () => moveNote(c),
      });
      // Moment ⇄ part. A one-tap way to make a zone (then drag the grip to fit)
      // and a one-tap way to collapse one — no dragging required for either.
      if (isZone) {
        items.push({
          label: 'Just this moment',
          icon: noteIcon.zone,
          onSelect: () => void setNoteSpan(c, c.at_s!, null),
        });
      } else if (stamped) {
        items.push({
          label: 'Make it cover a part',
          icon: noteIcon.zone,
          onSelect: () => {
            const d = state.duration;
            const end = d > 0 ? Math.min(d, c.at_s! + 8) : c.at_s! + 8;
            void setNoteSpan(c, c.at_s!, Math.max(c.at_s! + MIN_ZONE_S, end));
          },
        });
      }
      items.push({ label: 'Mark done', icon: noteIcon.done, onSelect: () => void setNoteDone(c, true) });
      items.push({ label: 'Delete', icon: noteIcon.del, onSelect: () => confirmDelete(c) });
    }

    const title = isZone
      ? `Note ${fmtTime(c.at_s!)}–${fmtTime(c.end_s!)}`
      : stamped
        ? `Note at ${fmtTime(c.at_s!)}`
        : 'This note';
    sheet(title, items);
  }

  /** Play the take from a note's moment — or just seek there if it's already on. */
  function goToNote(c: Comment): void {
    if (c.at_s == null) return;
    const v = sel();
    if (!v) return;
    if (player.isCurrent(v.id)) player.seek(c.at_s);
    else void playVersion(v, c.at_s);
  }

  /** Edit the words. Optimistic; puts the old text back if it doesn't save. */
  async function editNote(c: Comment): Promise<void> {
    const text = await promptSheet({
      title: 'Edit the note',
      label: 'What did you hear?',
      value: c.body,
      multiline: true,
      confirm: 'Save',
    });
    if (text == null || text === c.body) return;

    const old = c.body;
    c.body = text;
    render();
    try {
      await editComment(c.id, { body: text });
    } catch (e) {
      c.body = old;
      render();
      toast(e instanceof ApiError ? e.human : "That didn't save. Try again?", true);
    }
  }

  /** Tick a note off (it leaves the wave and the live list) — or bring it back. */
  async function setNoteDone(c: Comment, done: boolean): Promise<void> {
    const old = c.done;
    c.done = done;
    render();
    toast(done ? 'Ticked off.' : 'Back on the list.');
    try {
      await editComment(c.id, { done });
    } catch (e) {
      c.done = old;
      render();
      toast(e instanceof ApiError ? e.human : "That didn't save. Try again?", true);
    }
  }

  function confirmDelete(c: Comment): void {
    sheet('Delete this note?', [
      { label: 'Yes, delete it', icon: noteIcon.del, accent: true, onSelect: () => void deleteNote(c) },
    ]);
  }

  /** Delete a note for good. Optimistic; slots it back in on failure. */
  async function deleteNote(c: Comment): Promise<void> {
    const data = state.data;
    if (!data) return;
    const idx = data.comments.indexOf(c);
    if (idx < 0) return;

    data.comments.splice(idx, 1);
    render();
    try {
      await deleteComment(c.id);
      toast('Gone.');
    } catch (e) {
      data.comments.splice(idx, 0, c);
      render();
      toast(e instanceof ApiError ? e.human : "That didn't save. Try again?", true);
    }
  }

  /**
   * Re-time a note — the "slide it" the band asked for, made as simple as it can
   * be: get the song to the right spot and tap "Move here", or type the time.
   * A general note picks up a timestamp this way and grows a pin.
   */
  function moveNote(c: Comment): void {
    const root = document.getElementById('overlay')!;
    const scrim = h('div', { class: 'scrim' });
    const panel = h('div', { class: 'sheet', role: 'dialog', 'aria-label': 'Move this note' });

    let offTick: (() => void) | null = null;
    const close = (): void => {
      offTick?.();
      offTick = null;
      scrim.remove();
      panel.remove();
      document.removeEventListener('keydown', onKey);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') close();
    };

    // The big button carries the live playhead time so they can see exactly
    // where the note is about to land, and it updates as the song plays.
    const hereLabel = h('span', { text: '' });
    const paintHere = (): void => {
      hereLabel.textContent = `Move here — ${fmtTime(player.get().currentTime)}`;
    };
    paintHere();
    offTick = player.onTick(paintHere);

    const here = h('button', { class: 'btn-play note-move-here' }, [hereLabel]);
    here.addEventListener('click', () => {
      const t = player.get().currentTime;
      close();
      void applyMove(c, t);
    });

    const typed = h('input', {
      type: 'text',
      inputmode: 'numeric',
      value: c.at_s != null ? fmtTime(c.at_s) : '',
      placeholder: '2:41',
      'aria-label': 'Type a time like 2:41',
    }) as HTMLInputElement;
    const setBtn = h('button', { class: 'btn-sm', text: 'Set' });
    const applyTyped = (): void => {
      const secs = parseTime(typed.value);
      if (secs == null) {
        toast('Type a time like 2:41.', true);
        return;
      }
      close();
      void applyMove(c, secs);
    };
    setBtn.addEventListener('click', applyTyped);
    typed.addEventListener('keydown', (e) => {
      if ((e as KeyboardEvent).key === 'Enter') applyTyped();
    });

    panel.append(
      h('div', { class: 'grab' }),
      h('h3', { text: 'Move this note' }),
      h('p', { class: 'note-move-hint', text: 'Drops the note where the song is right now. Let it play to the spot, then tap Move here — or type the exact time below.' }),
      here,
      h('div', { class: 'note-move-type' }, [
        h('label', { text: 'or type a time' }),
        h('div', { class: 'note-move-typerow' }, [typed, setBtn]),
      ]),
    );

    scrim.addEventListener('click', close);
    document.addEventListener('keydown', onKey);
    root.append(scrim, panel);
  }

  /** Set a note's moment. Optimistic; re-sorts so it moves in the list too. A
   *  duration note slides whole — its length comes with it. */
  async function applyMove(c: Comment, at: number): Promise<void> {
    // Keep a zone's length so "move it" slides the whole part, not just its start.
    const span = c.end_s != null && c.at_s != null && c.end_s > c.at_s ? c.end_s - c.at_s : 0;
    let at_s = Math.max(0, Math.round(at * 10) / 10);
    if (state.duration > 0) at_s = Math.min(at_s, Math.max(0, state.duration - span));
    const end_s = span > 0 ? Math.round((at_s + span) * 10) / 10 : (c.end_s ?? null);
    if (c.at_s === at_s && (c.end_s ?? null) === (end_s ?? null)) return;

    const oldAt = c.at_s;
    const oldEnd = c.end_s ?? null;
    c.at_s = at_s;
    c.end_s = end_s;
    sortComments();
    render();
    toast(`Moved to ${fmtTime(at_s)}.`);
    try {
      await editComment(c.id, { at_s, end_s });
    } catch (e) {
      c.at_s = oldAt;
      c.end_s = oldEnd;
      sortComments();
      render();
      toast(e instanceof ApiError ? e.human : "That didn't save. Try again?", true);
    }
  }

  /** "2:41" → 161, "41" → 41, "1:02:03" → 3723. null if it's not a time. */
  function parseTime(raw: string): number | null {
    const s = raw.trim();
    if (!s) return null;
    const parts = s.split(':');
    if (parts.length > 3) return null;
    let secs = 0;
    for (const p of parts) {
      const t = p.trim();
      if (!/^\d+$/.test(t)) return null;
      secs = secs * 60 + Number(t);
    }
    return Number.isFinite(secs) && secs >= 0 ? secs : null;
  }

  async function noteAt(at: number | null): Promise<void> {
    const v = sel();
    if (!v) return;

    const stamped = at != null && Number.isFinite(at) && at > 0 && state.duration > 0;
    const at_s = stamped ? Math.round(at! * 10) / 10 : null;

    const text = await promptSheet({
      title: stamped ? `Note at ${fmtTime(at_s!)}` : 'Note about the song',
      label: stamped ? 'What did you hear?' : 'What did you want to say?',
      placeholder: stamped ? 'this ending' : 'the whole vibe of this one',
      multiline: true,
      confirm: 'Leave it',
    });
    if (!text) return;

    try {
      const c = await addComment(v.id, text, at_s);
      state.data?.comments.push(c);
      sortComments();
      render();
      toast('Noted.');
    } catch (e) {
      toast(e instanceof ApiError ? e.human : "That didn't save. Try again?", true);
    }
  }

  /* --------------------------------------------------- notes come alive (J6) */
  /*
   * The delight: while a take plays, the note the band is hearing RIGHT NOW
   * floats up over the wave — name + words — and then, when the moment passes,
   * it fades away. Like live comments scrolling past a stream, except every one
   * is a thing someone left about this exact second of this exact take.
   */

  /** The window a note is "live" for: [at_s, end_s] for a part, or a few
   *  seconds after the instant for a moment. Null when it can never light up. */
  function noteWindow(c: Comment): [number, number] | null {
    if (c.done || c.at_s == null) return null;
    const end = c.end_s != null && c.end_s > c.at_s ? c.end_s : c.at_s + POINT_WINDOW_S;
    return [c.at_s, end];
  }

  /** One floating note in the overlay. Tap it to open its menu. */
  function liveNoteEl(c: Comment): HTMLElement {
    const who = whoName(c);
    const main = h('div', { class: 'livenote-main' }, [
      who ? h('span', { class: 'livenote-who', text: who }) : null,
      h('span', { class: 'livenote-text', text: c.body }),
    ]);
    const el = h('div', { class: 'livenote' }, [
      who ? h('span', { class: 'livenote-avatar', text: initials(who) }) : null,
      main,
    ]);
    el.addEventListener('click', () => openNoteMenu(c));
    return el;
  }

  /**
   * Called every tick while this take is the one playing. Brings in notes whose
   * window we've entered, sends out the ones we've left — animating on the way
   * in and out, stacking when they overlap. Seeks are handled for free: it's
   * pure "what's live at time t", recomputed each frame.
   */
  function syncLiveNotes(t: number): void {
    const active = new Set<string>();
    if (state.data) {
      for (const c of state.data.comments) {
        if (c.version_id !== state.selected) continue;
        const w = noteWindow(c);
        if (w && t >= w[0] && t <= w[1]) active.add(c.id);
      }
    }

    // Bring in the newly-live (and rescue any that were fading but came back).
    for (const id of active) {
      const shown = liveShown.get(id);
      if (shown) {
        if (shown.leaving) {
          clearTimeout(shown.timer);
          shown.el.classList.remove('is-out');
          shown.leaving = false;
        }
        continue;
      }
      const c = state.data?.comments.find((x) => x.id === id);
      if (!c) continue;
      const el = liveNoteEl(c);
      liveLayer.appendChild(el);
      liveShown.set(id, { el, leaving: false, timer: 0 });
    }

    // Send out the ones whose moment has passed.
    for (const [id, rec] of liveShown) {
      if (active.has(id) || rec.leaving) continue;
      rec.leaving = true;
      rec.el.classList.add('is-out');
      rec.timer = window.setTimeout(() => {
        rec.el.remove();
        liveShown.delete(id);
      }, 320);
    }
  }

  /** Clear the overlay at once — used when we stop being the playing take. */
  function clearLiveNotes(): void {
    for (const rec of liveShown.values()) {
      clearTimeout(rec.timer);
      rec.el.remove();
    }
    liveShown.clear();
  }

  /* ------------------------------------------------- playback → the screen */

  function paintPlaying(): void {
    const v = sel();
    const p = player.get();
    const isThis = !!v && player.isCurrent(v.id);
    const playing = isThis && p.playing;

    vinyl?.setPlaying(playing);

    bigPlay.innerHTML = '';
    bigPlay.append(
      h('span', { class: 'bp-ic', html: playing ? icon.pause : icon.play }),
      h('span', { class: 'bp-label', text: playing ? 'Pause' : p.loading && isThis ? 'Loading' : 'Play' }),
    );
    bigPlay.dataset.loading = p.loading && isThis ? '1' : '0';

    for (const el of body.querySelectorAll<HTMLElement>('.version')) {
      el.dataset.current = el.dataset.version === p.now?.versionId ? '1' : '0';
      el.dataset.selected = el.dataset.version === state.selected ? '1' : '0';
    }

    const of = times.querySelector('.of');
    if (of) of.textContent = fmtTime(isThis ? p.duration || state.duration : state.duration);
    const atEl = times.querySelector('.at');
    if (atEl) atEl.textContent = fmtTime(isThis ? p.currentTime : 0);
  }

  const offState = player.subscribe(paintPlaying);

  const offTick = player.onTick((t, d) => {
    const v = sel();
    if (!v || !player.isCurrent(v.id)) {
      // Something else (or nothing) is playing — the overlay isn't ours to show.
      if (liveShown.size) clearLiveNotes();
      return;
    }
    vinyl?.setProgress(t, d);
    const atEl = times.querySelector('.at');
    if (atEl) atEl.textContent = fmtTime(t);
    syncLiveNotes(t);
  });

  // The karaoke driver rides its own ticker: it must also run when we're NOT the
  // playing take (to clear a stale highlight), which the note ticker above skips.
  const offLyricTick = player.onTick((t) => syncLyrics(t));

  void load();

  return {
    destroy() {
      abort.abort();
      offState();
      offTick();
      offLyricTick();
      clearTimeout(searchTimer);
      searchAbort?.abort();
      clearLiveNotes();
      host.removeEventListener('workbench:close', closeWorkbench);
      wbTeardown?.();
      wbTeardown = null;
      mixTeardown?.();
      mixTeardown = null;
      ws?.destroy();
      ws = null;
      vinyl?.destroy();
      vinyl = null;
      dawFileInput.remove();
    },
  };
}
